import { Typography } from "antd";
import { useTranslation } from "react-i18next";

import type { Endpoint } from "sandbox/types";

import { ERROR_CODES } from "sandbox/constants";
import { JsonViewer } from "sandbox/features/shared";

import s from "./EndpointDocs.module.css";

interface EndpointDocsProps {
  endpoint: Endpoint;
}

export const EndpointDocs = ({ endpoint }: EndpointDocsProps) => {
  const { t } = useTranslation("sandbox");
  const label = (key: string): string =>
    t(`components.endpointDocs.labels.${key}` as unknown as never);
  const th = (key: string): string =>
    t(`components.endpointDocs.tableHeaders.${key}` as unknown as never);
  const ec = (key: string): string =>
    t(`components.endpointDocs.errorCodes.${key}` as unknown as never);

  return (
    <div className={s.root}>
      <section className={s.section}>
        <Typography.Text
          className={s.sectionLabel}
          data-testid="endpoint-docs-method-title"
        >
          {endpoint.method} {endpoint.path}
        </Typography.Text>
        <p className={s.body}>{endpoint.description}</p>
      </section>

      {endpoint.pathParams && endpoint.pathParams.length > 0 && (
        <section className={s.section}>
          <Typography.Text className={s.sectionLabel}>
            {label("pathParameters")}
          </Typography.Text>
          {endpoint.pathParams.map(p => (
            <div className={s.paramWrapper} key={p.name}>
              <p className={s.paramName}>{p.name}</p>
              <p>{p.description}</p>
              {p.example && (
                <p>
                  ({t("labels.example")}: {p.example})
                </p>
              )}
            </div>
          ))}
        </section>
      )}

      {endpoint.queryParams && endpoint.queryParams.length > 0 && (
        <section
          className={s.section}
          data-testid="endpoint-docs-query-parameters"
        >
          <Typography.Text className={s.sectionLabel}>
            {label("queryParameters")}
          </Typography.Text>
          {endpoint.queryParams.map(p => (
            <div className={s.paramWrapper} key={p.name}>
              <p className={s.paramName}>{p.name}</p>
              <p>{p.description}</p>
              {p.example && (
                <p>
                  ({t("labels.example")}: {p.example})
                </p>
              )}
            </div>
          ))}
        </section>
      )}

      {endpoint.sampleBody !== undefined && (
        <section className={s.section}>
          <Typography.Text className={s.sectionLabel}>
            {label("requestBody")}
          </Typography.Text>
          <div className={s.codeBlock}>
            <JsonViewer data={endpoint.sampleBody} />
          </div>
        </section>
      )}

      <section className={s.section}>
        <Typography.Text className={s.sectionLabel}>
          {label("responseShape")}
        </Typography.Text>
        <div className={s.codeBlock}>
          <JsonViewer data={endpoint.sampleResponse} />
        </div>
      </section>

      <section className={s.section}>
        <Typography.Text className={s.sectionLabel}>
          {label("errorCodes")}
        </Typography.Text>
        <table className={s.table}>
          <thead>
            <tr>
              <th>{th("status")}</th>
              <th>{th("label")}</th>
              <th>{th("description")}</th>
            </tr>
          </thead>
          <tbody>
            {ERROR_CODES.map(err => (
              <tr key={err.code}>
                <td>
                  <code className={s.cell}>{err.code}</code>
                </td>
                <td>{label(err.labelKey)}</td>
                <td>{ec(err.descriptionKey)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};
